import { createContext } from 'react';

export type WebSocketStatus = 'connecting' | 'open' | 'closing' | 'closed';

export type TopicType = 'messagerie_dossier' | 'notification';

export type WebsocketType = {
    events: Array<WebsocketPayloadMessage>;
    topicType: TopicType;
    socket: WebSocket;
    status: WebSocketStatus;
};

export type BaseWebsocketData = {
    client: boolean;
    interne: boolean;
    reactions?: Array<{
        userIdentifier: string;
        icon: string;
        date: string;
    }>;
};

export type MessageItemDetailData = {
    type: 'message';
    message: string;
};

export type DocumentItemDetailData = {
    type: 'document';
    dosDocFileId: number;
    dosDocFileName: string;
    dosDocFileUrl: string;
};

export type IconItemDetailData = {
    type: 'emoji';
    icon: string;
};

export type MessageItemDetail = MessageItemDetailData | DocumentItemDetailData | IconItemDetailData;

export type WebsocketPayloadIsConnected = {
    payload: {
        token: string;
        userIdentifier: string;
    };
};

export type WebsocketPayloadCountMessages = {
    payload: {
        token: string;
        topicId: string;
    };
};

export type WebsocketPayloadGetUnreadMessages = {
    payload: {
        token: string;
        topicType?: TopicType;
    };
};

export type WebSocketPayloadGetLastMessages = {
    payload: {
        token: string;
        topicType?: TopicType;
        size?: number;
    };
};

export type WebsocketPayloadGetUnreadNotifications = {
    payload: {
        token: string;
        topicType?: TopicType;
        idTiers?: (string | number)[];
        contactId?: number;
    };
};

export type WebsocketPayloadSetMessagesRead = {
    payload: {
        token: string;
        messagesIds: string[];
        topicType: TopicType;
        topicId: string;
    };
};

export type WebsocketPayloadListMessage = {
    payload: {
        messageId?: string;
        token: string;
        topicId: string;
        userIdentifier: string;
        date?: string;
        discriminant?: {
            idPrescripteur: number;
            userIdentifierFormaliste: number[];
            idDossierFormalite: number;
        };
        message?: MessageItemDetail;
        from?: number;
        size?: number;
        query?: string;
    };
};

export type WebsocketPayloadMessage = {
    payload: {
        messageId?: string;
        token: string;
        topicType: TopicType;
        topicId: string;
        userIdentifier: string;
        date: string;
        discriminant?: {
            idPrescripteur: number;
            userIdentifierFormaliste: string;
            idDossierFormalite: number;
            [x: string]: unknown;
        };
        message: MessageItemDetail;
    };
};

export type WebsocketPayloadEditMessage = {
    payload: {
        messageId: string;
        token: string;
        message: string;
    };
};

export type WebsocketPayloadDeleteMessage = {
    payload: {
        messageId: string;
        token: string;
    };
};

export type MessageItem = {
    messageId: string;
    data: {
        topicType?: TopicType;
        discriminant: {
            [x: string]: unknown;
        };
        userIdentifier: string;
        topicId: string;
        messageId: string;
        date: string;
        reader: {
            client: 'true' | 'false' | true | false;
            interne: 'true' | 'false' | true | false;
            [x: string]: unknown;
        };
        message: MessageItemDetail;
    };
};

export type MessageResponse = {
    statusCode: number;
    body: string;
};

export type CountMessagesBodyResult = {
    action: string;
    topicId: string;
    count: number;
};

export type IsConnectedBodyResult = {
    action: string;
    isConnected: boolean;
    userIdentifier: string;
};

export type NewMessageBody = {
    action: string;
    message: MessageItem;
};

export type GetMessagesBodyResult = {
    action: string;
    messages: MessageItem[];
};

export type GetMessageBodyUnreadResult = {
    action: string;
    messages: MessageItem[];
};

export type TopicIdUnread = {
    topicId: string;
    count: number;
    oldestDocument: MessageItem;
};

export type TopicTypeUnread = {
    topicType: TopicType;
    count: number;
    totalTopics: number;
    topics: TopicIdUnread[];
};

export type UnreadResponse = {
    totalDocuments: number;
    topicTypes: TopicTypeUnread[];
};

export type GetNotificationBodyUnreadResult = {
    action: string;
    messages: UnreadResponse;
};

export type MessageReadPayload = {
    messagesIds: string[];
    topicType: TopicType;
    topicId: string;
};

export type WebsocketContextType = {
    baseWebsocketUrl: string | undefined;
    createSocket: () => void;
    sendOnSocket: (
        payload:
            | (WebsocketPayloadMessage & { action: string })
            | (WebsocketPayloadEditMessage & { action: string })
            | (WebsocketPayloadDeleteMessage & { action: string })
            | (WebsocketPayloadListMessage & { action: string })
            | (WebsocketPayloadCountMessages & { action: string })
            | (WebsocketPayloadIsConnected & { action: string })
            | (WebsocketPayloadSetMessagesRead & { action: string })
            | (WebsocketPayloadGetUnreadMessages & { action: string })
            | (WebsocketPayloadGetUnreadNotifications & { action: string })
            | (WebSocketPayloadGetLastMessages & { action: string }),
        actionExpected: string,
    ) => Promise<MessageResponse>;
    getSocket: () => WebSocket | null;
    hasSocket: () => boolean;
    socketOpen: boolean;
    messagesByTopic: Record<string, MessageItem[]>;
    unreadCountByTopic: Record<string, number>;
    isConnectedByUserIdentifier: Record<string, boolean>;
    sendMessage: (payload: WebsocketPayloadMessage) => Promise<void>;
    updateMessage: (payload: WebsocketPayloadEditMessage) => Promise<void>;
    deleteMessage: (payload: WebsocketPayloadDeleteMessage) => Promise<void>;
    getMessageCount: (topicId: string) => Promise<void>;
    setMessagesRead: (payload: MessageReadPayload) => Promise<void>;
    getIsConnected: (userIdentifier: string) => Promise<void>;
    fetchMessages: (
        topicId: string,
        topicType?: TopicType,
        from?: number,
        size?: number,
        query?: string,
    ) => Promise<void>;
    getUnreadMessages: (topicType?: TopicType) => Promise<void>;
    getUnreadItems: (topicType?: TopicType, contactId?: number) => Promise<void>;
    messagesUnread: MessageItem[];
    itemsUnread: UnreadResponse | null;
    getError: () => Record<string, unknown>;
    getLastMessages: (topicType?: TopicType, size?: number) => Promise<void>;
};

export const WebsocketContext = createContext<WebsocketContextType>({
    baseWebsocketUrl: undefined,
    createSocket: () => {},
    sendOnSocket: async () => ({ statusCode: 0, body: '' }),
    getSocket: () => null,
    hasSocket: () => false,
    socketOpen: false,
    messagesByTopic: {},
    unreadCountByTopic: {},
    isConnectedByUserIdentifier: {},
    sendMessage: async () => {},
    updateMessage: async () => {},
    deleteMessage: async () => {},
    getMessageCount: async () => {},
    setMessagesRead: async () => {},
    getIsConnected: async () => {},
    fetchMessages: async () => {},
    getUnreadMessages: async () => {},
    getUnreadItems: async () => {},
    messagesUnread: [],
    itemsUnread: null,
    getError: () => ({}),
    getLastMessages: async () => {},
});
